import React, { useEffect, useState, useCallback } from 'react';
import sqsService from '../services/sqsService';
import './SQSAdmin.css';

export default function SQSAdmin() {
    const [queues, setQueues]     = useState([]);
    const [loading, setLoading]   = useState(true);
    const [error, setError]       = useState(null);
    const [filter, setFilter]     = useState('');
    const [confirm, setConfirm]   = useState(null);
    const [purging, setPurging]   = useState(null);
    const [message, setMessage]   = useState(null);
    const [viewing, setViewing]     = useState(null);   // queue name being peeked
    const [viewMsgs, setViewMsgs]   = useState([]);
    const [viewLoading, setViewLoading] = useState(false);
    const [viewError, setViewError] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await sqsService.listQueues();
            setQueues(res.data || res || []);
        } catch (e) {
            setError(e.response?.data?.message || e.message);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const handlePurge = async (queueName) => {
        if (confirm !== queueName) { setConfirm(queueName); return; }
        setPurging(queueName);
        setConfirm(null);
        try {
            await sqsService.purgeQueue(queueName);
            setMessage({ type: 'success', text: `Queue "${queueName}" purged.` });
            load();
        } catch (e) {
            setMessage({ type: 'error', text: e.response?.data?.message || e.message });
        } finally {
            setPurging(null);
        }
    };

    const handleView = async (queueName) => {
        setViewing(queueName);
        setViewMsgs([]);
        setViewError(null);
        setViewLoading(true);
        try {
            const res = await sqsService.peekMessages(queueName);
            const data = res.data || res || {};
            setViewMsgs(data.messages || []);
        } catch (e) {
            setViewError(e.response?.data?.message || e.message);
        } finally {
            setViewLoading(false);
        }
    };

    const visible = queues
        .filter(q => !filter || q.name.toLowerCase().includes(filter.toLowerCase()))
        .sort((a, b) =>
            (b.messages || 0) - (a.messages || 0) ||
            (b.inflight || 0) - (a.inflight || 0) ||
            (b.delayed || 0) - (a.delayed || 0)
        );

    const totalMessages = queues.reduce((s, q) => s + (q.messages || 0), 0);
    const dlqCount = queues.filter(q => q.isDlq).length;

    return (
        <div className="acp-page">
            <div className="acp-header">
                <div>
                    <h1 className="acp-title">SQS Queues</h1>
                    <p className="acp-subtitle">
                        {queues.length} queues &mdash; {dlqCount} DLQs &mdash; {totalMessages.toLocaleString()} messages waiting
                    </p>
                </div>
                <button className="acp-btn acp-btn-secondary" onClick={load} disabled={loading}>
                    <svg width="14" height="14" viewBox="0 0 20 20" fill="none">
                        <path d="M4 10a6 6 0 0 1 6-6 6 6 0 0 1 4.24 1.76L16 4v4h-4l1.42-1.42A4 4 0 1 0 14 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    {loading ? 'Loading…' : 'Refresh'}
                </button>
            </div>

            {message && (
                <div className={`os-msg os-msg-${message.type}`} onClick={() => setMessage(null)}>
                    {message.text}
                </div>
            )}

            {error && <div className="os-msg os-msg-error">{error}</div>}

            <div className="acp-card">
                <div className="acp-card-header">
                    <span className="acp-card-title">All Queues</span>
                    <input
                        className="sqs-filter-input"
                        placeholder="Filter by name…"
                        value={filter}
                        onChange={e => setFilter(e.target.value)}
                    />
                </div>

                {loading ? (
                    <div className="acp-empty">Loading queues…</div>
                ) : visible.length === 0 ? (
                    <div className="acp-empty">No queues found.</div>
                ) : (
                    <table className="acp-table">
                        <thead>
                            <tr>
                                <th>Queue Name</th>
                                <th style={{ textAlign: 'right' }}>Messages</th>
                                <th style={{ textAlign: 'right' }}>In-flight</th>
                                <th style={{ textAlign: 'right' }}>Delayed</th>
                                <th>DLQ Target</th>
                                <th />
                            </tr>
                        </thead>
                        <tbody>
                            {visible.map(q => (
                                <tr key={q.name} className={q.isDlq ? 'sqs-dlq-row' : ''}>
                                    <td>
                                        <span className="sqs-queue-name">{q.name}</span>
                                        {q.isDlq && <span className="sqs-badge sqs-badge-dlq">DLQ</span>}
                                        {q.fifo && <span className="sqs-badge sqs-badge-fifo">FIFO</span>}
                                    </td>
                                    <td style={{ textAlign: 'right', fontWeight: q.messages > 0 ? '600' : 'normal', color: q.messages > 0 ? (q.isDlq ? '#991b1b' : '#111827') : '#9ca3af' }}>
                                        {q.messages.toLocaleString()}
                                    </td>
                                    <td style={{ textAlign: 'right', color: '#6b7280' }}>{q.inflight.toLocaleString()}</td>
                                    <td style={{ textAlign: 'right', color: '#6b7280' }}>{q.delayed.toLocaleString()}</td>
                                    <td className="sqs-redrive-target">{q.redriveTarget || <span style={{ color: '#d1d5db' }}>—</span>}</td>
                                    <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                                        {confirm === q.name ? (
                                            <span className="os-confirm-row">
                                                <span className="os-confirm-text">Purge all messages?</span>
                                                <button
                                                    className="acp-btn acp-btn-danger acp-btn-sm"
                                                    onClick={() => handlePurge(q.name)}
                                                    disabled={purging === q.name}
                                                >
                                                    {purging === q.name ? 'Purging…' : 'Confirm'}
                                                </button>
                                                <button
                                                    className="acp-btn acp-btn-secondary acp-btn-sm"
                                                    onClick={() => setConfirm(null)}
                                                >
                                                    Cancel
                                                </button>
                                            </span>
                                        ) : (
                                            <>
                                                <button
                                                    className="acp-btn acp-btn-secondary acp-btn-sm"
                                                    onClick={() => handleView(q.name)}
                                                    style={{ marginRight: 6 }}
                                                >
                                                    View
                                                </button>
                                                <button
                                                    className="acp-btn acp-btn-danger acp-btn-sm"
                                                    onClick={() => handlePurge(q.name)}
                                                    disabled={!!purging}
                                                >
                                                    Purge
                                                </button>
                                            </>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {viewing && (
                <div className="sqs-viewer-backdrop" onClick={() => setViewing(null)}>
                    <div className="sqs-viewer" onClick={e => e.stopPropagation()}>
                        <div className="sqs-viewer-header">
                            <span>
                                Messages in <strong>{viewing}</strong>
                                <span className="sqs-viewer-note"> — read-only peek, not dequeued (up to 10 shown)</span>
                            </span>
                            <button className="acp-btn acp-btn-secondary acp-btn-sm" onClick={() => setViewing(null)}>Close</button>
                        </div>
                        <div className="sqs-viewer-body">
                            {viewLoading ? (
                                <div className="acp-empty">Reading messages…</div>
                            ) : viewError ? (
                                <div className="os-msg os-msg-error">{viewError}</div>
                            ) : viewMsgs.length === 0 ? (
                                <div className="acp-empty">No messages returned (queue empty, or all currently in flight).</div>
                            ) : (
                                viewMsgs.map((m, i) => (
                                    <div key={m.messageId || i} className="sqs-msg">
                                        <div className="sqs-msg-meta">
                                            <span>#{i + 1}</span>
                                            {m.sentTimestamp && <span>sent {new Date(m.sentTimestamp).toLocaleString()}</span>}
                                            {m.receiveCount != null && <span>receiveCount {m.receiveCount}</span>}
                                            {m.s3Resolved && <span className="sqs-badge sqs-badge-fifo">S3-resolved</span>}
                                        </div>
                                        <pre className="sqs-msg-body">
                                            {typeof m.content === 'string' ? m.content : JSON.stringify(m.content, null, 2)}
                                        </pre>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
